import React from 'react';

import { observer } from 'mobx-react';
import { useAsync } from 'react-use';

import { angularizeWithProviders } from '../../../shared/providers';
import { useIsActivePage } from '../../../shared/hooks/useIsActivePage';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { solutionsStore } from '../../../shared/stores/solutionsStore';
import { challengeTutorialStore } from '../../store/challengeTutorialStore';
import { whatsNewStore } from '../../store/whatsNewStore';
import { ChallengeTutorialModal } from './ChallengeTutorialModal/ChallengeTutorialModal';
import { WhatsNewModal } from './WhatsNewModal/WhatsNewModal';

//List of pages where we don't need to show whats new
const bannedPages = [
  '/terms-and-conditions',
  '/self-billing/setup-card',
  '/self-billing/failed-payment',
  '/challenge-preview/:challengeId',
  '/challenge-landing/:challengeId',
  '/challenge-leaderboard/:challengeId',
  '/challenge-sign-up/:challengeId',
  '/nutrition-account-not-found',
  '/already-participant',
];

const NgzNotificationModals = observer(() => {
  const { loggedInUser, isEntity, isCoach } = loggedInUserDataStore;
  const { solutionTypeFlags, activeSolutionFlags } = solutionsStore;

  const getChallengeTutorialInfo = async (userId: number) => {
    if ((isEntity || isCoach) && solutionTypeFlags.CHALLENGE) {
      await Promise.all([
        challengeTutorialStore.getVideoUrl(),
        challengeTutorialStore.getModalStatus(userId),
      ]);
    }
  };

  const getWhatsNewInfo = async (userId: number) => {
    if (isEntity || isCoach) {
      await whatsNewStore.getModalStatus(userId);
    }
  };

  const getInformationAboutModals = async () => {
    if (loggedInUser?.id) {
      await Promise.all([
        getChallengeTutorialInfo(loggedInUser.id),
        getWhatsNewInfo(loggedInUser.id),
      ]);
    }
  };

  const { loading } = useAsync(getInformationAboutModals, [loggedInUser?.id]);

  const isBannedPage = useIsActivePage({ urls: bannedPages });

  if (isBannedPage || loading) {
    return null;
  }

  const canShowWhatsNew = whatsNewStore.open;
  const canShowChallengeTutorial =
    challengeTutorialStore.open && activeSolutionFlags.CHALLENGE;

  // order is important, represent priority of modals
  if (canShowChallengeTutorial && challengeTutorialStore.mode) {
    return (
      <ChallengeTutorialModal
        mode={challengeTutorialStore.mode}
        videoUrl={challengeTutorialStore.videoUrl}
        onClose={challengeTutorialStore.closeModal}
        showAgain={challengeTutorialStore.showAgain}
        onShowAgainChange={challengeTutorialStore.changeShowAgain}
      />
    );
  }

  if (canShowWhatsNew) {
    return <WhatsNewModal onClose={whatsNewStore.closeModal} />;
  }

  return null;
});

angularizeWithProviders(NgzNotificationModals, 'ngzNotificationModals', {});
